import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart as ChartJS, CategoryScale, Legend, ArcElement } from "chart.js";
import { PaymentMethod } from "../../../../../api/settlement";
import { useMemo, useState } from "react";
import { getPayTypeText } from "../../../../solution/utils/settlement.util";
import { SettlementDailyExpenseResponse } from "../../../../../api/settlement-daily";
ChartJS.register(CategoryScale, ArcElement, Legend, ChartDataLabels);

// 차드 나열 순서
export const HEADER_METHODS: PaymentMethod[] = [
  PaymentMethod.CASH,
  PaymentMethod.BANK_TRANSFER,
  PaymentMethod.CARD,
];

export const useDailyExpenseChart = (
  dailyExpense: SettlementDailyExpenseResponse | undefined
) => {
  // 해당 데이터가 더미 데이터인지 여부
  const [isNoData, setIsNoData] = useState(false);

  // 데이터 리스트 (비율)
  const rateData = useMemo(() => {
    if (!dailyExpense) return [];

    const paymentMap = new Map<PaymentMethod, number>();

    dailyExpense.expenses.forEach((expense) => {
      const prev = paymentMap.get(expense.paymentMethod) ?? 0;
      paymentMap.set(expense.paymentMethod, prev + expense.amount);
    });

    const values = HEADER_METHODS.map((method) => paymentMap.get(method) ?? 0);

    const sum = values.reduce((a, b) => a + b, 0);

    if (sum === 0) {
      setIsNoData(true);
      return [1, 1, 1];
    }

    setIsNoData(false);
    return values.map((v) => (v / sum) * 100);
  }, [dailyExpense]);

  // 라벨 리스트
  const labels = HEADER_METHODS.map((method) => getPayTypeText(method));

  // 도넛 차트 가운데 텍스트 추가
  const centerTextPlugin = {
    id: "centerText",
    beforeDraw(chart: any) {
      const { ctx, width, height } = chart;
      ctx.save();
      ctx.font = "700 32px Pretendard";
      ctx.fillStyle = "#212023";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("총지출", width / 2, height / 2);
      ctx.restore();
    },
  };

  const chartData = {
    labels: labels,
    datasets: [
      {
        data: rateData,
        backgroundColor: [
          "#7F25FB",
          "#C3B6FF",
          "#ECE9FF",
        ],
        datalabels: {
          labels: {
            name: {
              display: (context: any) =>
                context.dataset.data[context.dataIndex] !== 0,
              color: (context: any) => {
                const bgColor = Array.isArray(context.dataset.backgroundColor)
                  ? context.dataset.backgroundColor[context.dataIndex]
                  : context.dataset.backgroundColor;

                return bgColor === "#7F25FB" ? "#fff" : "#121212";
              },
              font: {
                size: 22,
                weight: 500,
                family: "Pretendard",
              },
              formatter: (value: any, context: any) => {
                const index = context.dataIndex;
                return labels[index];
              },
              borderRadius: 4,
              offset: 0,
              align: "top" as const,
            },
            value: {
              display: (context: any) =>
                context.dataset.data[context.dataIndex] !== 0,
              // 더미 데이터일 시 0.00으로 포맷
              formatter: (value: any) => {
                return isNoData ? "0.00%" : `${value.toFixed(2)}%`;
              },
              color: "#fff",
              font: {
                size: 20,
                weight: 700,
                family: "Pretendard",
              },
              backgroundColor: "#373636",
              borderRadius: 5,
              offset: 0,
              align: "bottom" as const,
            },
          },
        },
      },
    ],
  };

  const options = {
    cutout: 80,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    events: [],
  };

  return { chartData, options, centerTextPlugin };
};
